import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { useUserDetails } from "../hooks/useUserDetails";
import { useUserPermissions } from "../hooks/useUserPermissions";
import { db, usersCollection } from "../services/firebase";
import { useIdleTimer } from "react-idle-timer";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const CURRENT_VERSION = "1.1.1"; // Update this version with each new deployment
const IDLE_TIMEOUT = 2 * 60 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Function to log out user
  const handleLogout = async () => {
    await firebase.auth().signOut();
    setCurrentUser(null);
    navigate("/");
    alert("Session expired due to inactivity. You have been logged out.");
  };

  // Idle timer configuration
  const { reset } = useIdleTimer({
    timeout: IDLE_TIMEOUT,
    onIdle: handleLogout,
    debounce: 500,
  });

  useEffect(() => {
    reset();
  }, [location.pathname, reset]);

  useEffect(() => {
    const checkVersionAndAuth = async () => {
      const storedVersion = localStorage.getItem("appVersion");

      // If version mismatch, log out user and force re-authentication
      if (storedVersion !== CURRENT_VERSION) {
        await firebase.auth().signOut();
        localStorage.setItem("appVersion", CURRENT_VERSION);
        window.location.reload(true); // Force a hard refresh
        return;
      }

      // Firebase auth state listener
      const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
        if (user && user.email) {
          try {
            const snapshot = await db
              .collection("users")
              .where("email", "==", user.email)
              .where("employmentStatus", "in", ["Active", "Contract"])
              .get();

            if (!snapshot.empty) {
              const userData = snapshot.docs[0].data();

              // Check if user is inactive or contract-based
              if (["Inactive"].includes(userData.employmentStatus)) {
                await firebase.auth().signOut();
                setCurrentUser(null);
                navigate("/");
                alert(
                  "Your account is inactive. Please contact the administrator."
                );
              } else {
                setCurrentUser(user);
              }
            } else {
              console.warn("No user data found in Firestore.");
              await firebase.auth().signOut();
              setCurrentUser(null);
              alert("User not found. Please contact the administrator.");
            }
          } catch (error) {
            console.error("Error during authentication:", error);
            await firebase.auth().signOut();
            setCurrentUser(null);
            alert("An error occurred. Please try again later.");
          }
        } else {
          setCurrentUser(null);
        }
        setLoading(false);
      });

      return () => unsubscribe();
    };

    checkVersionAndAuth();
  }, [navigate]);

  // Fetch user details and permissions using hooks
  const { data: userDetails } = useUserDetails(currentUser?.email);
  const { data: userPermissions } = useUserPermissions(currentUser?.email);

  const updateUserSavedFilters = async (filtersObject) => {
    if (!currentUser || !userDetails || !userDetails.fullName) return;
    const docId = userDetails.fullName.replace(/\s+/g, "_");
    try {
      await usersCollection.doc(docId).update({
        savedFilters: filtersObject,
      });
      // Optionally, update local state here if you maintain one.
    } catch (error) {
      console.error("Error updating saved filters:", error);
    }
  };

  // New: Update last selected filter for a context.
  const updateLastSelectedFilter = async (context, filterName) => {
    if (!currentUser || !userDetails || !userDetails.fullName) return;
    const docId = userDetails.fullName.replace(/\s+/g, "_");
    try {
      await usersCollection.doc(docId).update({
        lastSelectedFilters: {
          ...userDetails.lastSelectedFilters,
          [context]: filterName,
        },
      });
    } catch (error) {
      console.error("Error updating last selected filter:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        userDetails,
        userPermissions: userPermissions?.permissions,
        updateUserSavedFilters,
        updateLastSelectedFilter,
        loading,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};
